import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Pagination,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  ManageAccounts as FirmAdminIcon,
  AccountCircle as UserIcon,
} from '@mui/icons-material';
import {
  useListFirmUsers,
  useInviteUserToFirm,
  useRemoveUserFromFirm,
  useUpdateUserRoleInFirm,
  type UserRole,
  type FirmResponse,
  type FirmUser,
  type InviteUserRequest,
} from '@taxassist/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { 
  canManageUsers, 
  canRemoveUser, 
  canEditUserRole,
  getAssignableRoles,
  getRoleDescription,
  getRoleDisplayName,
  USER_ROLES
} from '../../utils/userPermissions';
import { useAuth } from "../../contexts/AuthContext";


interface UsersTabProps {
  firm: FirmResponse;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: (userData: InviteUserRequest) => void;
  isLoading: boolean;
  currentUserRole: UserRole;
}

interface RemoveUserDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: FirmUser | null;
  isLoading: boolean;
}

interface EditUserRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (newRole: UserRole) => void;
  user: FirmUser | null;
  isLoading: boolean;
  currentUserRole: UserRole;
}

const getRoleIcon = (role: FirmUser['role']) => {
  switch (role) {
    case USER_ROLES.admin:
      return <AdminIcon color="primary" />;
    case USER_ROLES.firm_admin:
      return <FirmAdminIcon color="secondary" />;
    case USER_ROLES.firm_user:
      return <UserIcon color="action" />;
    default:
      return <PersonIcon />;
  }
};

// Use the centralized utility function for role display names

const getStatusColor = (status: FirmUser['status']): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'FORCE_CHANGE_PASSWORD':
      return 'warning';
    case 'DISABLED':
      return 'error';
    case 'UNCONFIRMED':
      return 'default';
    default:
      return 'default';
  }
};

export function UsersTab({ firm, onSuccess, onError }: UsersTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FirmUser | null>(null);
  
  const pageSize = 10;
  const { user: currentUser } = useAuth();
  const currentUserRole = currentUser?.role as UserRole;

  // API hooks
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useListFirmUsers(firm.firmId, {
    page: currentPage,
    limit: pageSize,
  });

  const inviteUserMutation = useInviteUserToFirm({
    mutation: {
      onSuccess: (data) => {
        onSuccess(`User ${data.email} invited successfully`);
        refetchUsers();
        setInviteDialogOpen(false);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Failed to invite user';
        onError(message);
      },
    },
  });

  const removeUserMutation = useRemoveUserFromFirm({
    mutation: {
      onSuccess: () => {
        onSuccess('User removed successfully');
        refetchUsers();
        setRemoveDialogOpen(false);
        setSelectedUser(null);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Failed to remove user';
        onError(message);
      },
    },
  });

  const updateUserRoleMutation = useUpdateUserRoleInFirm({
    mutation: {
      onSuccess: (data) => {
        onSuccess(`User role updated to ${getRoleDisplayName(data.newRole)} successfully`);
        refetchUsers();
        setEditRoleDialogOpen(false);
        setSelectedUser(null);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Failed to update user role';
        onError(message);
      },
    },
  });

  const handleInviteUser = useCallback((userData: InviteUserRequest) => {
    inviteUserMutation.mutate({
      firmId: firm.firmId,
      data: userData,
    });
  }, [firm.firmId, inviteUserMutation]);

  const handleRemoveUser = useCallback(() => {
    if (selectedUser) {
      removeUserMutation.mutate({
        firmId: firm.firmId,
        userId: selectedUser.userId,
      });
    }
  }, [firm.firmId, selectedUser, removeUserMutation]);

  const handleOpenRemoveDialog = useCallback((user: FirmUser) => {
    setSelectedUser(user);
    setRemoveDialogOpen(true);
  }, []);

  const handleOpenEditRoleDialog = useCallback((user: FirmUser) => {
    setSelectedUser(user);
    setEditRoleDialogOpen(true);
  }, []);

  const handleUpdateUserRole = useCallback((newRole: UserRole) => {
    if (selectedUser) {
      updateUserRoleMutation.mutate({
        firmId: firm.firmId,
        userId: selectedUser.userId,
        data: { role: newRole },
      });
    }
  }, [selectedUser, firm.firmId, updateUserRoleMutation]);

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const canManageUsersInFirm = canManageUsers(currentUserRole);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Users ({pagination?.total ?? users.length})
        </Typography>
        {canManageUsersInFirm && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User
          </Button>
        )}
      </Box>

      {/* Error Display */}
      {usersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users: {(usersError as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Users List */}
      <Paper elevation={1}>
        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invite users to get started
            </Typography>
          </Box>
        ) : (
          <List>
            {users.map((user, index) => (
              <div key={user.userId}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      {getRoleIcon(user.role)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Chip
                          label={getRoleDisplayName(user.role)}
                          color={user.role === USER_ROLES.admin ? 'primary' : user.role === USER_ROLES.firm_admin ? 'secondary' : 'default'}
                          size="small"
                        />
                        <Chip
                          label={user.status ? user.status.replace('_', ' ') : 'Unknown'}
                          color={getStatusColor(user.status)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <EmailIcon fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Invited by {user.invitedBy} on {new Date(user.createdAt).toLocaleDateString()}
                          {user.lastLoginAt && (
                            <> • Last login: {new Date(user.lastLoginAt).toLocaleDateString()}</>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {canManageUsersInFirm && canEditUserRole(currentUserRole, user.role, user.email === currentUser?.email) && (
                        <Tooltip title="Edit user role">
                          <IconButton
                            onClick={() => handleOpenEditRoleDialog(user)}
                            disabled={false} // Will be true when API is available
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageUsersInFirm && canRemoveUser(currentUserRole, user.role) && (
                        <Tooltip title="Remove user">
                          <IconButton
                            edge="end"
                            onClick={() => handleOpenRemoveDialog(user)}
                            disabled={removeUserMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < users.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInvite={handleInviteUser}
        isLoading={inviteUserMutation.isPending}
        currentUserRole={currentUserRole}
      />

      {/* Edit User Role Dialog */}
      <EditUserRoleDialog
        open={editRoleDialogOpen}
        onClose={() => {
          setEditRoleDialogOpen(false);
          setSelectedUser(null);
        }}
        onUpdate={handleUpdateUserRole}
        user={selectedUser}
        isLoading={updateUserRoleMutation.isPending}
        currentUserRole={currentUserRole}
      />

      {/* Remove User Dialog */}
      <RemoveUserDialog
        open={removeDialogOpen}
        onClose={() => {
          setRemoveDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleRemoveUser}
        user={selectedUser}
        isLoading={removeUserMutation.isPending}
      />
    </Box>
  );
}

function InviteUserDialog({ open, onClose, onInvite, isLoading, currentUserRole }: InviteUserDialogProps) {
  const assignableRoles = getAssignableRoles(currentUserRole);
  const defaultRole = assignableRoles.includes(USER_ROLES.firm_user) ? USER_ROLES.firm_user : assignableRoles[0];
  
  const [formData, setFormData] = useState<InviteUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    role: defaultRole,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = useCallback((field: keyof InviteUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(() => {
    if (validateForm()) {
      onInvite(formData);
    }
  }, [formData, validateForm, onInvite]);

  const handleClose = useCallback(() => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: defaultRole,
    });
    setErrors({});
    onClose();
  }, [onClose, defaultRole]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            required
            fullWidth
          />
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
            fullWidth
          />
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={handleChange('role')}
              error={!!errors.role}
            >
              {assignableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {getRoleDisplayName(role)}
                </MenuItem>
              ))}
            </Select>
            {formData.role && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {getRoleDescription(formData.role)}
                </Typography>
              </Box>
            )}
          </FormControl>
          <TextField
            label="Temporary Password (Optional)"
            type="password"
            value={formData.temporaryPassword || ''}
            onChange={handleChange('temporaryPassword')}
            helperText="If not provided, a secure password will be generated automatically"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
        >
          {isLoading ? 'Inviting...' : 'Invite User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveUserDialog({ open, onClose, onConfirm, user, isLoading }: RemoveUserDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove User</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to remove <strong>{user?.firstName} {user?.lastName}</strong> ({user?.email}) from this firm?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone. The user will lose access to this firm immediately.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {isLoading ? 'Removing...' : 'Remove User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditUserRoleDialog({ open, onClose, onUpdate, user, isLoading, currentUserRole }: EditUserRoleDialogProps) {
  const assignableRoles = getAssignableRoles(currentUserRole);
  const [selectedRole, setSelectedRole] = useState<UserRole>(user?.role || assignableRoles[0]);

  // Update selected role when user changes
  useEffect(() => {
    if (user?.role) {
      setSelectedRole(user.role);
    }
  }, [user?.role]);

  const handleSubmit = useCallback(() => {
    if (selectedRole && selectedRole !== user?.role) {
      onUpdate(selectedRole);
    } else {
      onClose();
    }
  }, [selectedRole, user?.role, onUpdate, onClose]);

  const handleClose = useCallback(() => {
    if (user?.role) {
      setSelectedRole(user.role);
    }
    onClose();
  }, [user?.role, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User Role</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Change role for <strong>{user?.firstName} {user?.lastName}</strong> ({user?.email})
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              label="Role"
              onChange={(e) => setSelectedRole(e.target.value as UserRole)}
            >
              {assignableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  <Box>
                    <Typography variant="body2">
                      {getRoleDisplayName(role)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getRoleDescription(role)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || selectedRole === user?.role}
          startIcon={isLoading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {isLoading ? 'Updating...' : 'Update Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}